import { useLocation } from '../contexts/LocationContext';
import { useNavigate } from 'react-router-dom';
import { Star, Mail, Flame, Tag } from 'lucide-react';
import { IMAGES } from '../shared/constants';
import { userService } from '../apiClient';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';

const FieldCard = ({ field, priority = false }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { location: userLocation } = useLocation();

  const { data: canOperateData } = useQuery({
    queryKey: ['can-operate', currentUser?.uid, field?.operatorID],
    queryFn: () => userService.canOperate(field.operatorID),
    enabled: Boolean(currentUser && field?.operatorID),
    staleTime: 60_000,
  });

  const isOwner = Boolean(canOperateData?.canOperate);

  const handleCardClick = () => navigate(`/field/${field.id}`);
  
  const handleActionClick = (e) => {
    e.stopPropagation();
    if (isOwner) {
      navigate(`/edit-listing/${field.id}`);
      return;
    }

    const target = `/field/${field.id}?book=1`;
    navigate(target);
  };

  const displayPrice = field.effectivePrice ?? field.pricePlaceholder;
  const formattedPrice = displayPrice ? Number(displayPrice).toFixed(0) : '0';
  const imageUrl = field.imagePath || IMAGES?.FIELD_PLACEHOLDER;
  const distanceText = field.getFormattedDistance(userLocation);

  return (
    <div 
      className="card h-100 border-0 shadow-sm field-card-hover rounded-4 overflow-hidden bg-white" 
      onClick={handleCardClick}
      style={{ cursor: 'pointer', transition: 'all 0.2s ease-in-out' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 1rem 3rem rgba(0,0,0,.1)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.05)'; }}
    >
      <div className="position-relative bg-light" style={{ height: '220px' }}>
        <img
          src={imageUrl}
          className="w-100 h-100 object-fit-cover"
          alt={field.name}
          loading={priority ? 'eager' : 'lazy'}
          fetchpriority={priority ? 'high' : 'auto'}
        />
        <div className="position-absolute top-0 start-0 w-100 p-3 d-flex justify-content-between">
          {field.isContactOnly() && (
            <span className="badge d-flex align-items-center gap-1 shadow-sm" style={{ backgroundColor: 'var(--field-green)' }}>
              <Mail size={12} /> Contact Only
            </span>
          )}
          {isOwner && <span className="badge bg-primary shadow-sm ms-auto">Your Listing</span>}
        </div>
      </div>

      <div className="card-body p-3 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start mb-1">
          <h5 className="card-title fw-bold text-dark mb-0 text-truncate w-100" title={field.name}>{field.name}</h5>
          {field.rating && <div className="d-flex align-items-center text-dark small fw-bold"><Star size={14} className="fill-black text-black me-1" /> {field.rating}</div>}
        </div>

        <div className="text-muted small mb-2 d-flex align-items-center">
           <span className="text-truncate" style={{maxWidth: distanceText ? '60%' : '100%'}}>{field.city}, {field.state}</span>
           {distanceText && <><span className="mx-1">•</span><span>{distanceText}</span></>}
        </div>

        <div className="mb-3 d-flex gap-2 flex-wrap">
           <span className="badge bg-light text-secondary border fw-normal px-2 py-1">{field.type}</span>
           <span className="badge bg-light text-secondary border fw-normal px-2 py-1">{field.surface}</span>
           {field.capacity && <span className="badge bg-light text-secondary border fw-normal px-2 py-1">{field.capacity}</span>}
        </div>

        <div className="mt-auto d-flex align-items-end justify-content-between pt-3 border-top">
          <div>
            <span className="fw-bold fs-5 text-dark">${formattedPrice}</span> <span className="text-muted small">/ hr</span>
            {field.rateType && field.rateType !== 'base' && (
              <span className={`badge ms-2 d-inline-flex align-items-center gap-1 ${field.rateType === 'surge' ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'}`}>
                {field.rateType === 'surge' ? <Flame size={11} /> : <Tag size={11} />}
                {field.rateType === 'surge' ? 'Surge' : 'Discount'}
              </span>
            )}
          </div>
          <button type="button" className={`btn btn-sm ${isOwner ? 'btn-outline-dark' : ''} rounded-pill px-4 fw-bold shadow-sm`} style={{backgroundColor: isOwner ? 'transparent' : 'var(--field-green)', borderColor: isOwner ? 'var(--text-primary)' : 'var(--field-green)', color: isOwner ? 'var(--text-primary)' : 'white'}} onClick={handleActionClick}>
            {isOwner ? 'Manage' : 'Book'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FieldCard;